import type { PlainMessage } from "@bufbuild/protobuf";
import { ConnectError } from "@connectrpc/connect";
import { captureException } from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
// ブラウザバックを使えなくするために、確認から支払い完了まで1ページで完結させる
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import type {
  FundSource,
  TransactionGroup,
} from "schema/gen/es/chiikipoint/model/v2/model_pb";
import type { PayRequest_PaymentDetail } from "schema/gen/es/chiikipoint/wallet/v2/service_pb";
import { z } from "zod";
import { useFooter } from "../../../contexts/footer-context";
import {
  PayComplete,
  PayConfirm,
  PayInput,
  RecentStoreConfirmModal,
  usePayForm,
} from "../../../features/pay";
import { requestStoreReviewAfterPayment } from "../../../features/pay/request-store-review";
import { getCurrentTime } from "../../../libs/date";
import { AppError, ERROR_CODES } from "../../../libs/errors";
import {
  getFundSourceAvailabilityMapForStore,
  sortItemsByFundSourceAvailability,
} from "../../../libs/fund-source-filters";
import { getSortedBalancesWithFundSources } from "../../../libs/get-sorted-balances-with-fund-sources";
import { t as translate } from "../../../libs/i18n";
import {
  isMissingPayScreenIssuedAt,
  isPayScreenStale,
} from "../../../libs/pay-route-guard";
import {
  getRecentStorePayment,
  setRecentStorePayment,
  shouldConfirmRecentStorePayment,
} from "../../../libs/recent-store-payment";

const paySearchSchema = z.object({
  // transform で t を string に変換すると、クッションのパラメータがうまく渡せないため、number を許可している
  t: z.union([z.string(), z.number()]).optional(),
  // QR遷移時に付与される支払い画面の発行時刻(ms)。復元された古い画面の失効判定に使う
  issuedAt: z.coerce.number().optional(),
});

type Mode = "input" | "confirm" | "complete";

export const Route = createFileRoute("/_authed/pay/$storeSlug")({
  validateSearch: (search) => paySearchSchema.parse(search),
  // 残高は loader で取得後 usePayForm の state に固定されるため、キャッシュが残ると
  // 支払い直後の再スキャンで支払い前の残高が表示される。離脱時に必ず破棄する
  gcTime: 0,
  loaderDeps: ({ search: { issuedAt, t } }) => ({ issuedAt, t }),
  loader: async ({
    context: { client, tenant },
    params: { storeSlug },
    deps: { issuedAt, t },
  }) => {
    const now = Date.now();

    // issuedAt 欠落時は読み込み時刻を付与して同一画面へ再遷移する（古いURLでも即失効させない）
    if (isMissingPayScreenIssuedAt(issuedAt)) {
      throw redirect({
        to: "/pay/$storeSlug",
        params: { storeSlug },
        search: {
          ...(t !== undefined ? { t } : {}),
          issuedAt: now,
        },
        replace: true,
      });
    }

    // 前日のURL等が復元された古い画面での誤決済を防ぐため、フェッチ前に失効判定する
    if (isPayScreenStale({ issuedAt, now })) {
      throw redirect({
        to: "/",
        state: {
          toast: {
            message: translate(
              "支払い画面の有効期限が切れました。お手数ですが、もう一度QRコードを読み取ってください。",
            ),
            type: "error",
          },
        },
      });
    }

    const [storeResponse, balanceResponse] = await Promise.all([
      client.getStore({ storeSlug }).catch((error) => {
        if (error instanceof ConnectError) {
          if (error.message === "[not_found] not_found") {
            toast.error(
              translate("この店舗はポイント支払いに対応していません"),
            );
            throw redirect({
              to: "/",
            });
          }
        }
        throw new AppError(ERROR_CODES.CONNECT_GET_STORE_FAILED, error.message);
      }),
      client.getMyBalances({}).catch((error) => {
        throw new AppError(
          ERROR_CODES.CONNECT_GET_MY_BALANCES_FAILED,
          error.message,
          {
            originalError: error,
          },
        );
      }),
    ]);

    const { store } = storeResponse;

    if (!store) {
      throw redirect({
        to: "/",
        state: {
          toast: { message: translate("店舗が見つかりません"), type: "error" },
        },
      });
    }

    const { sortedBalances, filtersByFundSourceId } =
      await getSortedBalancesWithFundSources(
        client,
        balanceResponse.balanceWithFundSources,
      );
    const initialBalances = sortedBalances.filter(
      (b) => b?.userBalance?.amount && b.userBalance.amount > 0,
    );

    const { availabilityByFundSourceId } = getFundSourceAvailabilityMapForStore(
      {
        store,
        fundSources: initialBalances
          .map((balance) => balance.fundSource)
          .filter((fundSource): fundSource is FundSource =>
            Boolean(fundSource),
          ),
        filtersByFundSourceId,
      },
    );
    const sortedInitialBalances = sortItemsByFundSourceAvailability({
      items: initialBalances,
      availabilityByFundSourceId,
    });

    const paymentFeatureDisabled = tenant.feature.payment === false;

    return {
      store,
      client,
      tenantSlug: tenant.slug,
      paymentFeatureDisabled,
      initialBalances: sortedInitialBalances,
      availabilityByFundSourceId,
    };
  },
  component: Pay,
});

function Pay() {
  const {
    store,
    client,
    tenantSlug,
    paymentFeatureDisabled,
    initialBalances,
    availabilityByFundSourceId,
  } = Route.useLoaderData();
  const { t } = Route.useSearch();
  const [mode, setMode] = useState<Mode>("input");
  const { setDisabled: setFooterDisabled } = useFooter();
  const [transactionGroup, setTransactionGroup] =
    useState<TransactionGroup | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const lastActivityRef = useRef<number>(Date.now());
  const nextActionRequestIdRef = useRef(0);
  const [isRecentStoreModalOpen, setIsRecentStoreModalOpen] = useState(false);

  const payForm = usePayForm({
    initialBalances,
    availabilityByFundSourceId,
  });

  // 支払い完了画面までフッターを非表示にする
  useEffect(() => {
    setFooterDisabled(true);

    if (mode === "complete") {
      setFooterDisabled(false);
    }

    return () => {
      setFooterDisabled(false);
    };
  }, [setFooterDisabled, mode]);

  // 5分間の無操作タイマー
  // モバイルスリープ等にも対応するため、refとDate.now()から経過時間を計算
  useEffect(() => {
    const checkInactivity = () => {
      const now = Date.now();
      const elapsed = now - lastActivityRef.current;

      if (elapsed >= 5 * 60 * 1000) {
        alert(
          translate(
            "長時間操作がなかったため、支払画面を閉じました。もう一度はじめからやり直してください。",
          ),
        );
        navigate({ to: "/", replace: true });
      }
    };

    const interval = setInterval(checkInactivity, 10 * 1000); // Check every 10 seconds

    const activityEvents = ["mousedown", "keydown", "touchstart"];
    const handleUserActivity = () => {
      lastActivityRef.current = Date.now();
    };

    for (const event of activityEvents) {
      window.addEventListener(event, handleUserActivity);
    }

    return () => {
      clearInterval(interval);
      for (const event of activityEvents) {
        window.removeEventListener(event, handleUserActivity);
      }
    };
  }, [navigate]);

  const handlePay = useCallback(async () => {
    try {
      if (payForm.payAmount === undefined) {
        throw new Error("amount is undefined");
      }

      const paymentDetails: PlainMessage<PayRequest_PaymentDetail>[] =
        payForm.balances
          .filter((b) => b.balanceState.amount > 0)
          .map((balance) => ({
            fundSourceId: balance.fundSourceId,
            amount: BigInt(balance.balanceState.amount),
          }));

      const response = await client.pay({
        storeId: store.id,
        paymentDetails: paymentDetails,
        qrId: t?.toString() ?? undefined,
      });

      try {
        const currentTime = await getCurrentTime();
        setRecentStorePayment({
          storeId: store.id,
          paidAt: currentTime.date.toISOString(),
        });
      } catch (storageError) {
        captureException(storageError);
        try {
          setRecentStorePayment({
            storeId: store.id,
            paidAt: new Date().toISOString(),
          });
        } catch (fallbackStorageError) {
          captureException(fallbackStorageError);
        }
      }

      return response;
    } catch (error) {
      if (error instanceof Error) {
        toast.error(
          `${translate("支払いに失敗しました")} \n[${error.message}]`,
        );
      } else {
        toast.error(translate("支払いに失敗しました"));
      }
      setMode("input");
      captureException(error);
      throw error;
    }
  }, [payForm, client, store.id, t]);

  const handleNext = useCallback(async () => {
    const requestId = nextActionRequestIdRef.current + 1;
    nextActionRequestIdRef.current = requestId;

    try {
      const currentTime = await getCurrentTime();
      const shouldConfirm = shouldConfirmRecentStorePayment({
        currentStoreId: store.id,
        currentTime: currentTime.date,
        recentStorePayments: getRecentStorePayment(),
      });

      if (nextActionRequestIdRef.current !== requestId) {
        return;
      }

      if (shouldConfirm) {
        setIsRecentStoreModalOpen(true);
        return;
      }
    } catch (error) {
      captureException(error);

      if (nextActionRequestIdRef.current !== requestId) {
        return;
      }
    }

    setMode("confirm");
  }, [store.id]);

  const handleConfirmRecentStorePayment = useCallback(() => {
    setIsRecentStoreModalOpen(false);
    setMode("confirm");
  }, []);

  const handleCancelRecentStorePayment = useCallback(() => {
    setIsRecentStoreModalOpen(false);
  }, []);

  return (
    <>
      {mode === "input" && (
        <PayInput
          payFormState={payForm}
          store={store}
          paymentFeatureDisabled={paymentFeatureDisabled}
          onNext={handleNext}
        />
      )}
      {mode === "confirm" && (
        <PayConfirm
          store={store}
          payForm={payForm}
          onBack={() => setMode("input")}
          pay={handlePay}
          onComplete={(tg) => {
            setTransactionGroup(tg);
            // 支払い完了時に取引履歴のキャッシュを破棄
            queryClient.resetQueries({ queryKey: ["transactions"] });
            setMode("complete");
            void requestStoreReviewAfterPayment(client, tenantSlug, tg);
          }}
        />
      )}
      {mode === "complete" && (
        <PayComplete
          store={store}
          payForm={payForm}
          transactionGroup={transactionGroup}
        />
      )}
      {mode === "input" && isRecentStoreModalOpen && (
        <RecentStoreConfirmModal
          store={store}
          onConfirm={handleConfirmRecentStorePayment}
          onCancel={handleCancelRecentStorePayment}
        />
      )}
    </>
  );
}
